import { useMemo, useState, useEffect } from 'react';
import Modal from '@shared/components/atoms/Modal/Modal';
import InputModal from '@shared/components/atoms/InputModal';
import {
  useVacationDetailQuery,
  useGetRemainVacationByEnumberQuery,
  useGetVacationFile,
  usePutVacationApprovalDoneMutation,
  usePutVacationApprovalReturnMuration,
} from '@features/vacation/hooks';

import { formatSimpleDate } from '@shared/utils/formatDate';
import safeToBase64Image from '@shared/lib/safeToBase64Image';
import { renderApproverSign } from '@shared/components/atoms/RenderApproverSign';

import styles from '@pages/vacation/components/VacationApprovalDetailModal/VacationApprovalDetailModal.module.scss';
import Button from '@shared/components/atoms/Button';
import Alert from '@shared/components/atoms/Alert';

const ALERT_MESSAGES = {
  'approve-confirm': '결재하시겠습니까?',
  'approve-done': '결재가 완료되었습니다.',
  'reject-done': '반려가 완료되었습니다.',
  'reject-empty': '반려 사유를 입력해주세요.',
  'reject-too-long': '반려 사유는 최대 30자까지 입력할 수 있습니다.',
  'download-denied': '사내망에서만 다운로드가 가능합니다.',
};

// TODO VacationCommonModal 사용하는것으로 변환할것
// props에 allList 추가
export default function VacationApprovalDetailModal({
  isOpen,
  onClose,
  onApproved,
  vacation,
  allList = [],
}) {
  const [alertState, setAlertState] = useState(null);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { mutate: approveVacation, isPending } = usePutVacationApprovalDoneMutation({
    onSuccess() {
      // 결재 성공 → 완료 Alert
      setAlertState('approve-done');
    },
    onError(error) {
      // 필요 시 에러 Alert 처리
      console.error(error);
    },
  });

  const {
    data: vacationData,
    isLoading,
    isFetching,
    isError,
  } = useVacationDetailQuery(vacation.idx);

  // 현재 목록(allList)을 활용한 중복 체크 로직
  const overlappedItems = useMemo(() => {
    if (!vacationData || !allList.length) return [];

    const currentStart = vacationData.start_date;
    const currentEnd = vacationData.end_date;
    const currentEnumber = vacation.enumber;
    const currentIdx = vacation.idx;

    return allList.filter((item) => {
      // 1. 자기 자신 제외
      if (item.idx === currentIdx) return false;
      // 2. 동일 사원인지 확인
      if (item.writerInfo?.enumber !== currentEnumber) return false;
      // 3. 유효한 상태인지 확인 (임시저장 0, 반려 4, 삭제 5, 관리자취소 7 제외)
      if (item.state === 0 || item.state === 4 || item.state === 5 || item.state === 7) {
        return false;
      }
      // 4. 날짜가 겹치는지 확인 (A시작 <= B종료 && A종료 >= B시작)
      const isDateOverlap = currentStart <= item.end_date && currentEnd >= item.start_date;

      return isDateOverlap;
    });
  }, [vacationData, allList, vacation.enumber, vacation.idx]);

  const isOverlapped = overlappedItems.length > 0;

  const { data: remainVacationData } = useGetRemainVacationByEnumberQuery(vacation.enumber);
  const { mutate: downloadFile } = useGetVacationFile({
    onError: (err) => {
      setAlertState('download-denied');
    },
  });
  const { mutate: rejectVacation, isPending: isRejectPending } =
    usePutVacationApprovalReturnMuration({
      onSuccess() {
        // 반려 성공 → 모달 닫고 완료 Alert
        setRejectModalOpen(false);
        setRejectReason('');
        setAlertState('reject-done');
      },
      onError(error) {
        console.error(error);
        alert('반려 처리 중 오류가 발생했습니다.');
      },
    });
  // 휴가 현황 데이터 가공
  const vacationStatus = useMemo(
    function () {
      const raw = remainVacationData?.resultData?.remainVacation;
      if (!raw || 'NO_DATA' === raw) {
        return {
          generated: { beginningOfYear: 0, afterRevision: 0, currentYearAdditional: 0, total: 0 },
          used: { previousYearPreUsed: 0, currentYearUsed: 0, total: 0 },
          remaining: { afterRevision: 0, total: 0 },
        };
      }

      const parts = raw.split('|').map(Number);

      // 발생분
      const beginningOfYear = parts[0] ?? 0; // 년초 발생분
      const afterRevision = parts[3] ?? 0; // 개정 후 발생분
      const currentYearAdditional = parts[4] ?? 0; // 당해 추가분
      // 사용분(반영)
      const previousYearPreUsed = parts[5] ?? 0; // 전년도 선사용분
      const currentYearUsed = parts[6] ?? 0; // 당해 사용분
      // 잔여분
      const remainingAfterRevision = parts[9] ?? 0; // 개정 후
      const totalRemaining = parts[10] ?? 0; // 총 잔여분

      return {
        generated: {
          beginningOfYear: beginningOfYear,
          afterRevision: afterRevision,
          currentYearAdditional: currentYearAdditional,
          total: beginningOfYear + afterRevision + currentYearAdditional,
        },
        used: {
          previousYearPreUsed: previousYearPreUsed,
          currentYearUsed: currentYearUsed,
          total: previousYearPreUsed + currentYearUsed,
        },
        remaining: {
          afterRevision: remainingAfterRevision,
          total: totalRemaining,
        },
      };
    },
    [remainVacationData],
  );

  //전자결재 서명
  const { writerBase64, signerBase64 } = useMemo(() => {
    if (!vacationData) {
      return {
        writerBase64: null,
        signerBase64: null,
      };
    }
    return {
      writerBase64: vacationData.writer_sign ? safeToBase64Image(vacationData.writer_sign) : null,

      signerBase64: vacationData.signer_sign ? safeToBase64Image(vacationData.signer_sign) : null,
    };
  }, [vacationData]);

  function handleFileClick(fileIdx) {
    downloadFile({ fileIdx });
  }

  function handleApprovalClick() {
    setAlertState('approve-confirm');
  }

  function handleApprovalConfirm() {
    approveVacation({
      idx: vacation.idx,
      comment: '',
    });
  }
  function handleAlertClose() {
    const isDone = alertState === 'approve-done' || alertState === 'reject-done';

    setAlertState(null);

    if (isDone) {
      if (onApproved) onApproved();
      else if (onClose) onClose();
    }
  }

  function handleRejectClick() {
    setRejectModalOpen(true);
  }

  function handleRejectConfirm() {
    if (!rejectReason.trim()) {
      setRejectModalOpen(false);
      setAlertState('reject-empty');
      return;
    }
    if (rejectReason.length > 30) {
      setRejectModalOpen(false);
      setAlertState('reject-too-long');
      return;
    }
    rejectVacation({
      idx: vacation.idx,
      comment: rejectReason,
    });
  }
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        showCloseButton={true}
        closeOnOverlayClick={false}
        classes={{
          modalBody: styles.vacationApprovalModalBody,
          modalContent: styles.vacationApprovalModalContent,
        }}
      >
        {isLoading || isFetching || isRejectPending || isPending ? (
          <div className={styles.loadingContainer}>
            <div className={styles.circularProgress} />
            <div className={styles.loadingMessage}>문서를 불러오는 중...</div>
          </div>
        ) : !vacationData ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingMessage}>데이터가 없습니다.</div>
          </div>
        ) : (
          <>
            <div className={styles.vacationApprovalContent}>
              <div className={styles.modalContent}>
                <div className={styles.scrollArea}>
                  {/* 고정 헤더: 휴가신청서 + 기안자/결재자 */}
                  <div className={styles.fixedHeader}>
                    <div className={styles.headerContainer}>
                      <div className={styles.headerBox}>
                        <div className={styles.headerTitle}>휴가신청서</div>
                        <div className={styles.approverContainer}>
                          {/* 신청자 */}
                          <div className={styles.signerBox}>
                            <span className={styles.signLabel}>기안자</span>
                            <div className={styles.signContent}>
                              <img
                                src={writerBase64 || undefined}
                                width={38}
                                height={38}
                                alt="신청자 전자서명 "
                              />
                            </div>
                          </div>
                          {/* 결재자 */}
                          <div className={styles.signerBox}>
                            <span className={styles.signLabel}>결재자</span>
                            <div className={styles.signContent}>
                              {renderApproverSign(vacationData.state, signerBase64, styles)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 스크롤 영역: 본문만 스크롤 */}

                  {/* 중복 신청 경고 */}
                  {isOverlapped && (
                    <div className={styles.overlapWarning}>
                      <strong>중복 신청 주의:</strong> 현재 결재 목록에 이 사원의 겹치는 휴가 신청이
                      {overlappedItems.length}건 더 있습니다.
                    </div>
                  )}
                  {vacationData.state == 7 && (
                    <div className={styles.overlapWarning}>관리자 취소 처리 된 문서입니다.</div>
                  )}
                  {/* 바디 영역 */}
                  <div className={styles.bodyContainer}>
                    <table className={styles.tableContainer}>
                      <tbody>
                        <tr>
                          <td>소속</td>
                          <td>{vacationData.writerInfo.team}</td>
                        </tr>
                        <tr>
                          <td>성명</td>
                          <td>{vacationData.writerInfo.name}</td>
                        </tr>
                        <tr>
                          <td>신청내역</td>
                          <td>
                            {vacationData.vacation_type.name || '-'} -
                            {vacationData.cc_type?.name ? ` ${vacationData.cc_type.name}` : ''}
                          </td>
                        </tr>
                        <tr>
                          <td>휴가기간</td>
                          <td>
                            {formatSimpleDate(vacationData.start_date)} ~
                            {formatSimpleDate(vacationData.end_date)} ({vacationData.period}일)
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" className={styles.reason}>
                            <div className={styles.reasonContent}>{vacationData.reason}</div>
                          </td>
                        </tr>
                        <tr>
                          <td>첨부파일</td>
                          <td>
                            {Array.isArray(vacationData.fileList) &&
                            0 < vacationData.fileList.length
                              ? vacationData.fileList.map((file, index) => (
                                  <div key={index}>
                                    <button
                                      onClick={() => handleFileClick(file.idx, file.name)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={styles.fileLink}
                                    >
                                      {file.name}
                                    </button>
                                  </div>
                                ))
                              : '없음'}
                          </td>
                        </tr>
                        <tr>
                          <td>참조자</td>
                          <td>
                            {Array.isArray(vacationData.recipientInfos) &&
                            0 < vacationData.recipientInfos.length
                              ? vacationData.recipientInfos.map((r) => r.name).join(', ')
                              : '없음'}
                          </td>
                        </tr>

                        {vacationData.reject_reason && (
                          <tr>
                            <td>코멘트</td>
                            <td>{vacationData.reject_reason}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className={styles.statusTableContainer}>
                      <table className={styles.statusTable}>
                        <thead>
                          <tr>
                            <th colSpan="3">발생분</th>
                            <th colSpan="2">사용분(반영)</th>
                            <th colSpan="2">잔여분</th>
                          </tr>
                          <tr>
                            <th>년초 발생분</th>
                            <th>
                              개정 후<br /> 발생분
                            </th>
                            <th>당해 추가분</th>
                            <th>
                              전년
                              <br /> 선사용분
                            </th>
                            <th>당해 사용분</th>
                            <th>개정 후</th>
                            <th className={styles.bold}>총 잔여분</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{vacationStatus.generated.beginningOfYear}</td>
                            <td>{vacationStatus.generated.afterRevision}</td>
                            <td>{vacationStatus.generated.currentYearAdditional}</td>
                            <td>{vacationStatus.used.previousYearPreUsed}</td>
                            <td>{vacationStatus.used.currentYearUsed}</td>
                            <td>{vacationStatus.remaining.afterRevision}</td>
                            <td className={styles.bold}>{vacationStatus.remaining.total}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className={styles.bottomContainer}>
                      <div className={styles.bottomBox}>
                        <div className={styles.bottomTitle}>위와 같이 휴가를 신청합니다.</div>
                        <div className={styles.bottomContent}>
                          {formatSimpleDate(vacationData.start_date, { style: 'korean' })}
                          <br />
                          신청인 : {vacationData.writerInfo.name}
                        </div>
                      </div>
                      <div className={styles.bottomCompany}>
                        (주) {vacationData.writerInfo.company}귀중
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 고정 푸터: 반려/결재 버튼 */}
              {(vacationData.state === 1 || vacationData.state === 2) && (
                <div className={styles.fixedFooter}>
                  <div className={styles.approvalBtn}>
                    <Button className={styles.return} onClick={handleRejectClick}>
                      반려
                    </Button>

                    <Button
                      className={styles.approval}
                      onClick={handleApprovalClick}
                      disabled={isPending}
                    >
                      결재
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <Alert
          isOpen={alertState !== null}
          mode={alertState === 'approve-confirm' ? 'confirm' : 'alert'}
          message={alertState ? ALERT_MESSAGES[alertState] : ''}
          onConfirm={alertState === 'approve-confirm' ? handleApprovalConfirm : undefined}
          onClose={handleAlertClose}
        />
      </Modal>
      <InputModal
        isOpen={rejectModalOpen}
        title="수정 요청 또는 반려 사유를 입력해 주세요."
        value={rejectReason}
        onChange={setRejectReason}
        placeholder="반려 사유를 입력해주세요."
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        onConfirm={handleRejectConfirm}
      ></InputModal>
    </>
  );
}
